import { API_BASE_URL } from '../config/env';

type Context={page:'AI'|'TRADE'|'MODAL';state:string;symbol:string};
type Sample={proof:string|null;rttMs:number|null;connectionStatus:'OK'|'TIMEOUT'|'FAILED'|'UNRECORDED'};
type Report=Context&Sample&{id:string;clientAt:string;event:string;detail:string;orderId?:string};
type Queued={wallet:string;report:Report};
const base=`${API_BASE_URL}${import.meta.env.DEV?'':'/api'}/v1`;
const storageKey='panta-connection-reports-v1';
type Attempt={c:Context;wallet:string;at:string;sample:Sample};
const attemptKey=storageKey+'-orders';
const attempts=new Map<string,Promise<Attempt>>();
function saveAttempt(key:string,attempt:Attempt|null){
 try{const saved=JSON.parse(localStorage.getItem(attemptKey)||'{}');if(attempt)saved[key]=attempt;else delete saved[key];
 const recent=Object.entries(saved).filter(([,v])=>Date.now()-Date.parse((v as Attempt).at)<86400000).slice(-100);
 localStorage.setItem(attemptKey,JSON.stringify(Object.fromEntries(recent)));}catch{}
}
let credentials:()=>{wallet:string;token:string}=()=>({wallet:'',token:''});
let context:(()=>Context)|null=null;
let timer:ReturnType<typeof setInterval>|undefined;
let pending:Promise<Sample>|null=null;
export type ConnectionSnapshot={rttMs:number|null;connectionStatus:Sample['connectionStatus'];completedAt:number};
let snapshot:ConnectionSnapshot={rttMs:null,connectionStatus:'UNRECORDED',completedAt:0};
const subscribers=new Set<(value:ConnectionSnapshot)=>void>();
export function subscribeConnection(read:(value:ConnectionSnapshot)=>void){
 subscribers.add(read);read({...snapshot});return ()=>{subscribers.delete(read);};
}
let flushing=false;
let queue:Queued[]=[];
let lastWindow='';
/** Probe proofs are single-use after POST; keep them out of later uploads. */
const usedProofs=new Set<string>();
const blank=():Sample=>({proof:null,rttMs:null,connectionStatus:'UNRECORDED'});
const visible=()=>typeof document==='undefined'||!document.hidden;
const uuid=()=>{try{if(typeof crypto.randomUUID==='function')return crypto.randomUUID();const b=crypto.getRandomValues(new Uint8Array(16));b[6]=(b[6]&15)|64;b[8]=(b[8]&63)|128;return Array.from(b,(n,i)=>([4,6,8,10].includes(i)?'-':'')+n.toString(16).padStart(2,'0')).join('');}catch{return '';}};
const consumeProof=(s:Sample)=>{
 if(s.connectionStatus!=='OK'||!s.proof)return;
 usedProofs.add(s.proof);
 if(usedProofs.size>80){const first=usedProofs.values().next().value;if(first)usedProofs.delete(first);}
};
/** OK needs a fresh proof. Soft/heartbeat may yield to ORDER_ACCEPTED on the same probe. */
const canEnqueue=(s:Sample,orderId?:string)=>{
 if(s.connectionStatus!=='OK')return true;
 if(!s.proof||usedProofs.has(s.proof))return false;
 if(orderId)return true;
 return !queue.some(x=>x.report.proof===s.proof);
};
function save(){try{localStorage.setItem(storageKey,JSON.stringify(queue.slice(-100)));}catch{/* Diagnostics cannot block trading. */}}
export function configureConnectionDiagnostics(read:typeof credentials){
 credentials=read;
 try{const saved=JSON.parse(localStorage.getItem(attemptKey)||'{}');for(const [key,value] of Object.entries(saved)){const a=value as Attempt;if(a?.wallet&&a?.sample&&Date.now()-Date.parse(a.at)<86400000)attempts.set(key,Promise.resolve(a));}}catch{}
 try{const saved=JSON.parse(localStorage.getItem(storageKey)||'[]');if(Array.isArray(saved)){
  queue=[];
  for(const x of saved){
   if(!x?.report?.id||typeof x.wallet!=='string'||Date.now()-Date.parse(x.report.clientAt)>=86400000)continue;
   // Drop invalid OK/null-proof leftovers that would 400 forever.
   if(x.report.connectionStatus==='OK'&&!x.report.proof)continue;
   if(x.report.proof&&queue.some(q=>q.report.proof===x.report.proof)){
    if(x.report.orderId)queue=queue.filter(q=>q.report.proof!==x.report.proof||q.report.orderId);
    else continue;
   }
   queue.push(x);
  }
  queue=queue.slice(-100);
 }}catch{}
}
function flush(){
 const {wallet,token}=credentials();if(flushing||!token||!wallet)return;
 queue=queue.filter(x=>Date.now()-Date.parse(x.report.clientAt)<86400000);
 const own=queue.filter(x=>x.wallet===wallet.toLowerCase());
 const item=own.find(x=>x.report.orderId)||own[0];if(!item)return;
 flushing=true;
 // 诊断上报：超时放宽，失败静默；400/409 仍出队，勿挡交易
 try{uni.request({url:base+'/connection-reports',method:'POST',data:item.report,timeout:8_000,
  header:{Authorization:`Bearer ${token}`,'Content-Type':'application/json'},
  success:r=>{if(r.statusCode===204||[400,409].includes(r.statusCode)){
   consumeProof(item.report);queue=queue.filter(x=>x!==item);save();
  }},
  fail:()=>{},complete:()=>{flushing=false;if(!queue.includes(item))setTimeout(flush,200);},
 });}catch{flushing=false;}
}
/** 进页挤业务时 5s 易超时显示「已取消」；略放宽并短窗复用上次样本 */
const PROBE_TIMEOUT_MS = 8_000;
const PROBE_REUSE_MS = 4_000;
export function probeConnection(opts?:{force?:boolean;exclusive?:boolean}):Promise<Sample>{
 // Soft events share in-flight probes; exclusive (order) always needs a fresh single-use proof.
 if(pending&&!opts?.exclusive)return pending;
 if(!opts?.force&&!opts?.exclusive&&snapshot.completedAt&&Date.now()-snapshot.completedAt<PROBE_REUSE_MS&&snapshot.connectionStatus!=='UNRECORDED'){
  return Promise.resolve({proof:null,rttMs:snapshot.rttMs,connectionStatus:snapshot.connectionStatus});
 }
 const run=new Promise<Sample>(resolve=>{
  const start=performance.now();
  uni.request({url:base+'/connection-probe?nonce='+encodeURIComponent(uuid()),method:'GET',timeout:PROBE_TIMEOUT_MS,
   success:r=>{
    const body=r.data as {proof?:string};
    resolve(r.statusCode===200&&typeof body?.proof==='string'?{proof:body.proof,rttMs:Math.round(performance.now()-start),connectionStatus:'OK'}:{...blank(),connectionStatus:'FAILED'});
   },fail:e=>resolve({...blank(),connectionStatus:/timeout/i.test(e.errMsg||'')?'TIMEOUT':'FAILED'}),
  });
 }).catch(()=>({...blank(),connectionStatus:'FAILED' as const})).then(sample=>{
  snapshot={rttMs:sample.rttMs,connectionStatus:sample.connectionStatus,completedAt:Date.now()};
  for(const read of subscribers){try{read({...snapshot});}catch{/* UI cannot interrupt diagnostics. */}}
  return sample;
 });
 if(opts?.exclusive)return run;
 pending=run.finally(()=>{pending=null;});
 return pending;
}
function enqueue(c:Context,wallet:string,sample:Sample,event:string,detail='',orderId?:string,clientAt=new Date().toISOString()){
 const id=uuid();if(!id||!wallet||credentials().wallet.toLowerCase()!==wallet)return;
 // Reused UI samples strip proof → 400; same proof twice → 400/409. Order evidence wins the probe.
 if(!canEnqueue(sample,orderId))return;
 if(sample.proof&&orderId)queue=queue.filter(x=>x.report.proof!==sample.proof||!!x.report.orderId);
 queue.push({wallet,report:{...c,...sample,id,clientAt,event,detail,orderId}});queue=queue.slice(-100);save();flush();
}
/** 心跳保持 10s，便于及时发现断链 */
const HEARTBEAT_INTERVAL_MS = 10_000;
/** WINDOW_STATE 等软事件节流；心跳不受此限 */
const WINDOW_REPORT_MIN_GAP_MS = 12_000;
let lastWindowReportAt = 0;

export function recordConnectionEvent(event:string,detail=''){
 if(!context||!visible())return;
 const c=context(),wallet=credentials().wallet.toLowerCase(),at=new Date().toISOString();
 if(!wallet||!credentials().token)return;
 // 预测态切换很密：节流窗内完全跳过（勿再 probe 挤连接）
 if (event === 'WINDOW_STATE' && Date.now() - lastWindowReportAt < WINDOW_REPORT_MIN_GAP_MS) {
  return;
 }
 if (event === 'WINDOW_STATE') lastWindowReportAt = Date.now();
 // 心跳要真实 RTT；软事件可复用短窗样本
 const forceProbe = event === 'HEARTBEAT' || event === 'CLIENT_ERROR';
 void probeConnection({force:forceProbe}).then(s=>enqueue(c,wallet,s,event,detail,undefined,at));
}
export function startConnectionDiagnostics(read:()=>Context){
 stopConnectionDiagnostics();context=read;lastWindow='';lastWindowReportAt=0;
 connectionTick();timer=setInterval(connectionTick,HEARTBEAT_INTERVAL_MS);
 if(typeof document!=='undefined')document.addEventListener?.('visibilitychange',onDiagnosticsVisibility);
 if(typeof window!=='undefined'){window.addEventListener('error',clientError);window.addEventListener('unhandledrejection',clientError);window.addEventListener('online',flush);}
}
/** 定时心跳：只走 record（内含一次 probe），勿再叠 probe/flush */
function connectionTick(){if(!context||!visible())return;recordConnectionEvent('HEARTBEAT');}
/** 回前台：强制刷新延迟并冲队列，不额外再打 HEARTBEAT（避免与 10s 定时叠报） */
function onDiagnosticsVisibility(){
 if(!visible()||!context)return;
 void probeConnection({force:true}); // skip short-window reuse; still shares in-flight pending
 flush();
}
let lastErrorAt=0;
function clientError(){if(Date.now()-lastErrorAt<10000)return;lastErrorAt=Date.now();recordConnectionEvent('CLIENT_ERROR','FRONTEND_EXCEPTION');}
export function stopConnectionDiagnostics(){
 clearInterval(timer);timer=undefined;context=null;
 if(typeof document!=='undefined')document.removeEventListener?.('visibilitychange',onDiagnosticsVisibility);
 if(typeof window!=='undefined'){window.removeEventListener('error',clientError);window.removeEventListener('unhandledrejection',clientError);window.removeEventListener('online',flush);}
}
export function connectionWindowChanged(){
 if(!context)return;const c=context(),key=`${c.page}:${c.symbol}:${c.state}`;
 if(key===lastWindow)return;lastWindow=key;recordConnectionEvent('WINDOW_STATE');
}
// Probe starts alongside the order request, never awaited by the order pipeline.
export function connectionOrderRequest(path:string,method:string,clientOrderId?:string){
 const submit=path==='/v1/orders'&&method==='POST';
 const lookup=method==='GET'&&/^\/v1\/orders\/[^/]+$/.test(path)||method==='POST'&&path==='/v1/orders/submission';
 if(!submit&&!lookup)return ()=>{};
 const wallet=credentials().wallet.toLowerCase(),key=wallet+':'+clientOrderId;
 if(submit&&context&&clientOrderId&&!attempts.has(key)){
  const c=context(),at=new Date().toISOString();
  const attempt=probeConnection({exclusive:true}).then(sample=>{const a={c,wallet,at,sample};saveAttempt(key,a);return a;});
  if(attempts.size>=100)attempts.delete(attempts.keys().next().value!);
  attempts.set(key,attempt);
 }
 const direct=submit&&context&&!clientOrderId?{c:context(),wallet,at:new Date().toISOString(),sample:probeConnection({exclusive:true})}:null;
 return (status:number,body?:unknown)=>{
  const b=body as {id?:string;orderId?:string;clientOrderId?:string;status?:string}|undefined;const orderId=b?.orderId||b?.id;
  if(status<200||status>=300||!orderId)return;
  if(path==='/v1/orders/submission'&&b?.status!=='ACCEPTED')return;
  const savedKey=wallet+':'+(b?.clientOrderId||clientOrderId),attempt=attempts.get(savedKey);
  if(attempt){
   attempts.delete(savedKey);
   void attempt.then(a=>{saveAttempt(savedKey,null);enqueue(a.c,a.wallet,a.sample,'ORDER_ACCEPTED','',orderId,a.at);});
  }else if(direct)void direct.sample.then(s=>enqueue(direct.c,wallet,s,'ORDER_ACCEPTED','',orderId,direct.at));
 };
}
export function connectionApiResult(path:string,status:number){
 if(status>0&&status<400)return;
 if(!/^\/v1\/(ai\/|insurance\/|orders|account|balance)/.test(path))return;
 // Static path and numeric status only: never store response bodies, credentials or query strings.
 const safe=path.split('?')[0].replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi,':id').slice(0,110);
 const key=`${safe}:${status}`,at=Date.now();
 if(lastApiError?.key===key&&at-lastApiError.at<10000)return;
 lastApiError={key,at};recordConnectionEvent('API_ERROR',`${safe} ${status}`);
}
let lastApiError:{key:string;at:number}|null=null;
